import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { type Player, type RegistrationForm } from '@iziping/common';
import { useCreatePreRegistration, useRegistrationData, useUpdatePreRegistration } from './registrationHooks';
import { apiClient } from '@iziping/common';
import { ENDPOINTS } from '@/model/endpoints';
import { useTranslation } from 'react-i18next';
import styles from './Registration.module.css';
import SubmitButton from '../shared/SubmitButton';
import { useNotification } from '@iziping/ui';
import { NotFound } from '@iziping/ui';
import { ChevronDown, ChevronUp, ImageIcon, ScrollText } from 'lucide-react';

const fetchUserByLicence = async (licence: string): Promise<Player> => {
    const response = await apiClient.get(ENDPOINTS.searchFFTT(licence));
    return response.data;
};

const RegistrationPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { uuid, registrationId } = useParams();
    const isEditMode = !!registrationId;
    const [licence, setLicence] = useState('');
    const [posterVisible, setPosterVisible] = useState(false);
    const [reglementVisible, setReglementVisible] = useState(false);

    const {
        control,
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<RegistrationForm>({
        defaultValues: {
            heats: [],
            id: '',
            licence: '',
            firstname: '',
            lastname: '',
            points: 500,
            club: '',
            email: '',
            message: '',
        },
    });
    const { data: formData, isLoading, error } = useRegistrationData(uuid, registrationId);
    const createMutation = useCreatePreRegistration();
    const updateMutation = useUpdatePreRegistration();

    const { notify } = useNotification();

    useEffect(() => {
        if (formData) {
            reset({
                id: formData.id,
                licence: formData.licence,
                firstname: formData.firstname,
                lastname: formData.lastname,
                club: formData.club,
                email: formData.email,
                points: formData.points,
                heats: formData.heats || [],
                message: formData.message || '',
            });
        }
    }, [formData, reset]);

    const handleLicenceBlur = async () => {
        if (!licence) return;

        try {
            const player = await fetchUserByLicence(licence); // Appel API
            setValue('licence', player.licence);
            setValue('firstname', player.firstname);
            setValue('lastname', player.lastname);
            setValue('club', player?.club?.name ?? '');
            setValue('points', player?.points!);
        } catch (error) {
            console.error('Error calling api:', error);
        }
    };

    const maxSelection = formData?.tournament?.maxHeat ?? 30;

    const handleHeatChange = (
        checked: boolean,
        heatId: number,
        fieldValue: number[],
        fieldOnChange: (v: number[]) => void
    ) => {
        if (checked) {
            if (fieldValue.length < maxSelection) {
                fieldOnChange([...fieldValue, heatId]);
            } else {
                notify(t('registration.heats.limit'), 'error');
            }
        } else {
            fieldOnChange(fieldValue.filter((v) => v !== heatId));
        }
    };

    const onSubmit = async (data: RegistrationForm) => {
        try {
            const response = isEditMode
                ? await updateMutation.mutateAsync({
                      registrationUUID: registrationId!,
                      tournamentUUID: uuid!,
                      request: data,
                  })
                : await createMutation.mutateAsync({
                      tournamentUUID: uuid!,
                      request: data,
                  });

            navigate(`/registration-confirmation/${response.id}`);
        } catch (e: any) {
            notify(t('registration.submit.error'), 'error');
        }
    };

    if (isLoading) return <p>{t('REGISTRATION_LOADING')}</p>;
    if (error) return <NotFound context="registration" />;
    if (!formData) return null;

    const config = formData.formConfig;

    const pageStyle = {
        ...(config?.primaryColor && { '--reg-primary': config.primaryColor }),
    } as React.CSSProperties;

    return (
        <div className={styles.pageWrapper} style={pageStyle}>
            <div className={config?.posterUrl ? styles.splitLayout : undefined}>
                {config?.posterUrl && (
                    <div className={`${styles.posterColumn}${!posterVisible ? ` ${styles.posterHidden}` : ''}`}>
                        <img src={config.posterUrl} alt={formData.tournament.name} />
                    </div>
                )}
                <form className={styles.registrationContainer} onSubmit={handleSubmit(onSubmit)}>
                    {config?.logoUrl && (
                        <div className={styles.formHeader}>
                            <img src={config.logoUrl} alt="logo" className={styles.formHeaderLogo} />
                        </div>
                    )}
                    <h2>{`${t('REGISTRATION_TITLE')} ${formData.tournament.name}`}</h2>
                    {formData?.stats && (
                        <div className={styles.statsCard}>
                            <p>
                                {t('REGISTRATION_NB_REGISTERED')} : <strong>{formData.stats.nbRegistered}</strong>
                            </p>
                            <ul className={styles.statsList}>
                                {Object.entries(formData.stats.registeredByHeats).map(([heat, count]) => (
                                    <li key={heat}>
                                        <span className={styles.heatName}>{heat}</span>
                                        <span className={styles.heatCount}>{count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className={styles.registrationGroup}>
                        <label htmlFor="licence">{t('REGISTRATION_LICENCE')}</label>
                        <input
                            {...register('licence', { required: false })}
                            onChange={(e) => setLicence(e.target.value)}
                            onBlur={handleLicenceBlur}
                        />
                    </div>
                    <div className={styles.registrationGroup}>
                        <label htmlFor="firstname">{t('REGISTRATION_FIRSTNAME')}</label>
                        <input
                            {...register('firstname', {
                                required: {
                                    value: true,
                                    message: t('registration.firstname.required'),
                                },
                            })}
                        />
                        {errors.firstname && <span className={styles.inputError}>{errors.firstname.message}</span>}
                    </div>

                    <div className={styles.registrationGroup}>
                        <label htmlFor="name">{t('REGISTRATION_LASTNAME')}</label>
                        <input
                            {...register('lastname', {
                                required: {
                                    value: true,
                                    message: t('registration.lastname.required'),
                                },
                            })}
                        />
                        {errors.lastname && <span className={styles.inputError}> {errors.lastname.message}</span>}
                    </div>

                    <div className={styles.registrationGroup}>
                        <label htmlFor="points">{t('REGISTRATION_POINTS')}</label>
                        <input
                            type={'number'}
                            defaultValue={500}
                            {...register('points', {
                                required: {
                                    value: true,
                                    message: t('registration.points.required'),
                                },
                            })}
                        />
                        {errors.points && <span className={styles.inputError}> {errors.points.message}</span>}
                    </div>

                    <div className={styles.registrationGroup}>
                        <label htmlFor="club">{t('REGISTRATION_CLUB')}</label>
                        <input {...register('club', { required: false })} />
                    </div>

                    <div className={styles.registrationGroup}>
                        <label htmlFor="email">{t('REGISTRATION_EMAIL')}</label>
                        <input
                            {...register('email', {
                                required: {
                                    value: true,
                                    message: t('registration.email.required'),
                                },
                            })}
                        />
                        {errors.email && <span className={styles.inputError}>{errors.email.message}</span>}
                    </div>
                    <div className={styles.registrationGroup}>
                        <label htmlFor="heats">{t('REGISTRATION_HEATS')}</label>
                        <Controller
                            control={control}
                            name="heats"
                            rules={{ required: t('registration.heats.required') }}
                            defaultValue={[]}
                            render={({ field }) => (
                                <div className={styles.checkboxGroup}>
                                    {formData?.tournament?.heats?.map((heat) => {
                                        const heatId = Number(heat.id);
                                        const isChecked = field.value?.includes(heatId) || false;
                                        const wasOriginallyChecked = (formData.heats ?? []).includes(heatId);
                                        const nbPreRegistered = formData.stats?.registeredByHeats?.[heat.name] ?? 0;
                                        const isFull =
                                            heat.playersLimit !== null &&
                                            nbPreRegistered >= heat.playersLimit &&
                                            !isChecked &&
                                            !wasOriginallyChecked;
                                        return (
                                            <label
                                                key={heatId}
                                                className={`${styles.checkboxItem}${isFull ? ` ${styles.checkboxItemDisabled}` : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={isFull}
                                                    onChange={(e) =>
                                                        handleHeatChange(
                                                            e.target.checked,
                                                            heatId,
                                                            field.value || [],
                                                            field.onChange
                                                        )
                                                    }
                                                />
                                                <span className={styles.checkboxLabel}>
                                                    {heat.name}
                                                    {isFull && (
                                                        <span className={styles.checkboxFull}>
                                                            {' '}
                                                            ({t('REGISTRATION_HEAT_FULL')})
                                                        </span>
                                                    )}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        />
                        {errors.heats && <span className={styles.inputError}>{errors.heats.message}</span>}
                    </div>
                    <div className={styles.registrationGroup}>
                        <label htmlFor="message">{t('REGISTRATION_MESSAGE')}</label>
                        <textarea rows={5} {...register('message', { required: false })} />
                    </div>
                    <SubmitButton
                        isSubmitting={isSubmitting}
                        label={isEditMode ? t('REGISTRATION_UPDATE') : t('REGISTRATION_VALIDATE')}
                        loadingLabel={t('REGISTRATION_LOADING')}
                    />
                </form>
                {config?.posterUrl && (
                    <button
                        type="button"
                        className={styles.posterToggleBtn}
                        onClick={() => setPosterVisible((v) => !v)}
                    >
                        <ImageIcon size={16} />
                        {posterVisible ? t('REGISTRATION_POSTER_HIDE') : t('REGISTRATION_POSTER_SHOW')}
                        {posterVisible ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                )}
            </div>
            {config?.rulesUrl && (
                <div className={styles.reglementSection}>
                    <button
                        type="button"
                        className={styles.reglementToggleBtn}
                        onClick={() => setReglementVisible((v) => !v)}
                    >
                        <ScrollText size={16} />
                        {reglementVisible ? t('REGISTRATION_RULES_HIDE') : t('REGISTRATION_RULES_SHOW')}
                        {reglementVisible ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {reglementVisible && (
                        <div className={styles.reglementViewer}>
                            <iframe
                                src={config.rulesUrl}
                                width="100%"
                                height="700px"
                                title={t('REGISTRATION_RULES_TITLE')}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RegistrationPage;
